import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core';
import flowRight from 'lodash-es/flowRight';
import { FormField } from 'core/components/form';
import withSpacing from 'core/components/form/with-spacing';
import styles from 'core/components/form/form.styles';
import lienHolders from './lien-holders.json';

// Default Formik field names this autocomplete writes to. Callers whose form stores the
// lienholder under differently-named fields (e.g. the Add-Car form uses `lienholder*`-prefixed
// fields to avoid colliding with the car fields in the same form) override via `fieldNames`.
const DEFAULT_FIELD_NAMES = {
  name: 'name',
  address: 'address',
  address2: 'address2',
  city: 'city',
  state: 'state',
  zip: 'zip',
  loanNumber: 'loanNumber'
};

const LienHolderAutoComplete = ({ setValues, currentValues, fieldNames }) => {
  const names = useMemo(() => ({ ...DEFAULT_FIELD_NAMES, ...fieldNames }), [fieldNames]);

  const options = useMemo(
    () => (lienHolders ? lienHolders.map(({ name, rn, ...suggestion }) => ({ text: name, id: rn, suggestion })) : []),
    []
  );

  const handleSelection = useCallback(
    (_, selection) => {
      const { suggestion, text } = selection || {};
      if (!suggestion) {
        return;
      }

      const { address, addressLine2, city, state, zip, loanNumber } = suggestion;

      setValues({
        ...currentValues,
        [names.name]: text || '',
        [names.address]: address || '',
        [names.address2]: addressLine2 || '',
        [names.city]: city || '',
        [names.state]: state || '',
        [names.zip]: zip || '',
        [names.loanNumber]: loanNumber || currentValues?.[names.loanNumber] || ''
      });
    },
    [setValues, currentValues, names]
  );

  return (
    <FormField
      type="autocomplete"
      name={names.name}
      id={names.name}
      mode="light"
      label="Name"
      options={options}
      allowUserInput
      fast={false}
      onSelection={handleSelection}
      permissions={{ isLicensedAction: false }}
    />
  );
};

LienHolderAutoComplete.propTypes = {
  setValues: PropTypes.func.isRequired,
  currentValues: PropTypes.object.isRequired,
  fieldNames: PropTypes.shape({
    name: PropTypes.string,
    address: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
    loanNumber: PropTypes.string
  })
};

export default flowRight(withStyles(styles), withSpacing)(LienHolderAutoComplete);
